import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, VStack, HStack, Input, FormControl, FormLabel,
  Button, SimpleGrid, useBreakpointValue,
} from '@chakra-ui/react';
import { FiLogOut } from 'react-icons/fi';
import { upsertPedagogicalExit } from 'services/qrService';
import { buildNormalizedGroupOptions, getUserGroups } from 'utils/courseUtils';
import StaffPicker from 'components/Search/StaffPicker';
import ExitTargetsFields from 'components/PedagogicalExit/ExitTargetsFields';
import { useUserAuth } from 'utils/site';

// Registro rápido de "Salida Pedagógica" (cursos y/o alumnos individuales,
// con rango de fechas) desde el dashboard — misma lógica que
// PedagogicalExitsTab.js, acotada a los campos imprescindibles para
// programarla sobre la marcha.
const NewPedagogicalExitModal = ({ isOpen, onClose, onSuccess, onError }) => {
  const { user: currentStaffUser } = useUserAuth();
  const [title, setTitle] = useState('');
  const [groupNames, setGroupNames] = useState([]);
  const [students, setStudents] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [responsible, setResponsible] = useState(null);
  // undefined = todavía no tocado (se muestra currentStaffUser por defecto);
  // null = el usuario lo limpió a propósito con "Cambiar" (debe quedar vacío,
  // no volver a autocompletarse solo). No usar null para ambos casos: con
  // `authorizedBy ?? currentStaffUser`, "Cambiar" (que dispara onSelect(null))
  // recaía siempre en currentStaffUser y el botón parecía no hacer nada.
  const [authorizedBy, setAuthorizedBy] = useState(undefined);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTitle('');
    setGroupNames([]);
    setStudents([]);
    setStartsAt('');
    setEndsAt('');
    setResponsible(null);
    setAuthorizedBy(undefined);
    (async () => {
      try {
        const rawGroups = await getUserGroups();
        setCourseOptions(buildNormalizedGroupOptions(rawGroups));
      } catch {
        // Sin glosario disponible — el selector quedará vacío
      }
    })();
  }, [isOpen]);

  const authorizedByPerson = authorizedBy === undefined ? currentStaffUser : authorizedBy;
  const canSave = !!title.trim() && (groupNames.length > 0 || students.length > 0) && !!startsAt && !!endsAt;

  // Modal más ancho en desktop para aprovechar 2 columnas — en mobile se
  // queda angosto y todo el contenido sigue apilado en una sola columna.
  const modalSize = useBreakpointValue({ base: 'md', md: '3xl', xl: '5xl' });

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await upsertPedagogicalExit({
        title: title.trim(),
        group_names: groupNames,
        student_ids: students.map(s => ({ id: s.id, name: s.name, username: s.username })),
        starts_at: startsAt,
        ends_at: endsAt,
        responsible_name: responsible?.name?.trim() || null,
        authorized_by: authorizedByPerson?.name?.trim() || null,
        active: true,
      });
      onSuccess?.(`Salida pedagógica «${title.trim()}» programada`);
      onClose();
    } catch (e) {
      onError?.(e?.response?.data?.errors ? JSON.stringify(e.response.data.errors) : e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size={modalSize} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent mx={3} minH={{ md: '620px' }}>
        <ModalHeader>Programar salida pedagógica</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* 2 columnas en desktop (md+): izquierda = a quién cubre la
              salida, derecha = cuándo y quién la autoriza. En mobile se
              apila en una sola columna. */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} alignItems="start">
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm">Título</FormLabel>
                <Input size="sm" borderRadius="8px" placeholder="Ej: Salida a museo 4° Básico"
                  value={title} onChange={(e) => setTitle(e.target.value)} />
              </FormControl>

              <ExitTargetsFields
                groupNames={groupNames}
                onGroupNamesChange={setGroupNames}
                students={students}
                onStudentsChange={setStudents}
                courseOptions={courseOptions}
              />
            </VStack>

            <VStack spacing={4} align="stretch">
              <HStack spacing={3} align="flex-start">
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Desde</FormLabel>
                  <Input type="datetime-local" size="sm" borderRadius="8px"
                    value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Hasta</FormLabel>
                  <Input type="datetime-local" size="sm" borderRadius="8px"
                    value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel fontSize="sm">Responsable</FormLabel>
                <StaffPicker value={responsible} onSelect={setResponsible} placeholder="Buscar responsable…" />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Autorizado por</FormLabel>
                <StaffPicker value={authorizedByPerson} onSelect={setAuthorizedBy} placeholder="Buscar quien autoriza…" />
              </FormControl>
            </VStack>
          </SimpleGrid>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancelar</Button>
          <Button
            variant="educational-primary" size="sm" onClick={handleSave}
            isLoading={saving} isDisabled={!canSave} leftIcon={<FiLogOut />}
          >
            Programar salida
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NewPedagogicalExitModal;
